import React, { useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  ListItemText,
  Button,
  Box,
  TextareaAutosize,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import style from "./DataCorrectionGrid.module.css";

import CustomSwitch from "../CustomSwitch/CustomSwitch";
import {
  getPickerViews,
  normalizeFormat,
} from "../../pages/Subject/DynamicForm/helper";
import { getDobAgeSelecterData } from "../../utilities/data";

// optional if you already have these helpers in your file/project
// import NumericPadInput from "./NumericPadInput";
// import { formatInitials } from "../utils/formatInitials";
// import { normalizeFormat, getPickerViews } from "../utils/dateHelpers";

const genderGrpArray = ["Male", "Female"];

const formatInitials = (value) => {
  if (!value) return "";

  const trimmed = value.trim();

  if (trimmed.length === 2) {
    return `${trimmed[0]}-${trimmed[1]}`;
  }

  return trimmed;
};

const DataCorrectionGrid = ({
  tableData = [],
  control,
  register,
  errors = {},
  disabled = false,
}) => {
  const [recordDataState, setRecordDataState] = useState({
    patientDobFormat: "",
    recordPatientDob: false,
    recordPatientGender: false,
    recordPatientInitials: false,
    useTwentyFourHourTime: "",
  });

  const {
    patientDobFormat,
    recordPatientDob,
    recordPatientGender,
    recordPatientInitials,
    useTwentyFourHourTime,
  } = recordDataState;

  const renderField = (field, rowIndex) => {
    const {
      fieldType,
      requestedValueFieldName,
      options,
      specialProps,
      currentValue,
      fieldName,
      removeFieldName,
    } = field;

    const name =
      requestedValueFieldName ||
      `requestedValue_${field?.answer_id || rowIndex}`;

    const commonError =
      errors?.[name]?.message || errors?.requestedValues?.[rowIndex]?.message;

    const validationRules = getValidationRules(field);

    switch (fieldType) {
      case "text": {
        const fieldReg = register(name, validationRules);

        return (
          <div>
            <TextareaAutosize
              minRows={3}
              className="form-text-area"
              disabled={disabled}
              {...fieldReg}
              onBlur={(e) => {
                if (fieldName.toLocaleLowerCase()?.includes("initials")) {
                  const formattedValue = formatInitials(e.target.value);
                  e.target.value = formattedValue;

                  fieldReg.onChange({
                    ...e,
                    target: {
                      ...e.target,
                      value: formattedValue,
                      name,
                    },
                  });
                }

                fieldReg.onBlur(e);
              }}
              style={{ width: "100%" }}
            />
            {commonError && <p className={style.errorText}>{commonError}</p>}
          </div>
        );
      }

      case "dobAgeSelector":
        return (
          <div>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              render={({ field: controllerField }) => (
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  disabled={disabled}
                  value={controllerField.value || ""}
                  onChange={(e) => controllerField.onChange(e.target.value)}
                  inputRef={controllerField.ref}
                  onBlur={controllerField.onBlur}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(getDobAgeSelecterData() || []).map((item, index) => (
                    <MenuItem key={index} value={item?.key}>
                      {item?.value}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {commonError && <p className={style.errorText}>{commonError}</p>}
          </div>
        );

      case "ageDateRange": {
        const minDate = specialProps?.minDate;
        const maxDate = specialProps?.maxDate;
        const dynamicFormat = normalizeFormat(patientDobFormat);
        const { views, openTo } = getPickerViews(dynamicFormat);

        return (
          <div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name={name}
                control={control}
                rules={validationRules}
                defaultValue={null}
                render={({ field: controllerField }) => (
                  <DatePicker
                    sx={{ width: "100%" }}
                    disabled={disabled}
                    format={dynamicFormat}
                    views={views}
                    openTo={openTo}
                    value={
                      controllerField.value
                        ? dayjs(controllerField.value, dynamicFormat)
                        : null
                    }
                    onChange={(date) => {
                      if (!date) {
                        controllerField.onChange(null);
                        return;
                      }

                      if (!dynamicFormat.includes("DD")) {
                        if (
                          dynamicFormat.includes("MMM") &&
                          dynamicFormat.includes("YYYY")
                        ) {
                          controllerField.onChange(date.format("MMM/YYYY"));
                        } else if (dynamicFormat.includes("YYYY")) {
                          controllerField.onChange(date.format("YYYY"));
                        }
                        return;
                      }

                      controllerField.onChange(date.format(dynamicFormat));
                    }}
                    minDate={minDate ? dayjs(minDate) : undefined}
                    maxDate={maxDate ? dayjs(maxDate) : undefined}
                    slotProps={{
                      textField: {
                        placeholder: dynamicFormat,
                        error: !!commonError,
                        helperText: commonError || "",
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </div>
        );
      }

      case "TimePicker":
        return (
          <div>
            <TextField
              size="small"
              fullWidth
              type="time"
              disabled={disabled}
              {...register(name, validationRules)}
              error={!!commonError}
              helperText={commonError || ""}
            />
          </div>
        );

      case "ageRangeYear": {
        const minValue = specialProps?.minValue;
        const maxValue = specialProps?.maxValue;

        return (
          <div>
            <TextField
              size="small"
              fullWidth
              type="number"
              disabled={disabled}
              inputProps={{
                maxLength: 4,
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: minValue,
                max: maxValue,
              }}
              error={!!commonError}
              helperText={commonError || ""}
              {...register(name, {
                ...validationRules,
                validate: (value) => {
                  if (!value) return true;
                  if (!/^\d{1,4}$/.test(value))
                    return "Only 1-4 digit whole numbers allowed";
                  if (parseInt(value, 10) < 0)
                    return "Negative values not allowed";
                  if (minValue !== undefined && parseInt(value, 10) < minValue)
                    return `Value must be at least ${minValue}`;
                  if (maxValue !== undefined && parseInt(value, 10) > maxValue)
                    return `Value must be at most ${maxValue}`;
                  return true;
                },
                onChange: (e) => {
                  const raw = e.target.value;
                  e.target.value = raw.replace(/\D/g, "").slice(0, 4);
                },
              })}
            />
          </div>
        );
      }

      case "checkbox":
        return (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {(options || []).map((item, index) => {
                const isObject = typeof item === "object" && item !== null;
                const value = isObject ? item.value : item;
                const text = isObject ? item.text : item;

                return (
                  <FormControlLabel
                    key={value ?? index}
                    control={
                      <Checkbox
                        {...register(name, validationRules)}
                        value={value}
                        disabled={disabled}
                      />
                    }
                    label={text}
                  />
                );
              })}
            </div>
            {commonError && <p className={style.errorText}>{commonError}</p>}
          </div>
        );

      case "Date":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              defaultValue={null}
              render={({ field: controllerField }) => (
                <DatePicker
                  sx={{ width: "100%" }}
                  disabled={disabled}
                  format="DD-MM-YYYY"
                  value={
                    controllerField.value
                      ? dayjs(controllerField.value, "YYYY-MM-DD")
                      : null
                  }
                  onChange={(date) => {
                    const formatted = date ? date.format("YYYY-MM-DD") : null;
                    controllerField.onChange(formatted);
                  }}
                  slotProps={{
                    textField: {
                      placeholder: "DD-MM-YYYY",
                      error: !!commonError,
                      helperText: commonError || "",
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        );

      case "eq5d5l":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              defaultValue={null}
              render={({ field: controllerField }) => (
                <DatePicker
                  sx={{ width: "100%" }}
                  shouldDisableDate={(date) => {
                    if (specialProps) {
                      const today = dayjs();
                      return !date.isSame(today, "day");
                    }
                    return false;
                  }}
                  disabled={disabled}
                  format="DD-MM-YYYY"
                  value={
                    controllerField.value
                      ? dayjs(controllerField.value, "YYYY-MM-DD")
                      : null
                  }
                  onChange={(date) => {
                    const formatted = date ? date.format("YYYY-MM-DD") : null;
                    controllerField.onChange(formatted);
                  }}
                  slotProps={{
                    textField: {
                      placeholder: "DD-MM-YYYY",
                      error: !!commonError,
                      helperText: commonError || "",
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        );

      case "singleCheckbox": {
        const checkboxLabel = specialProps || "Yes";

        return (
          <div>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              defaultValue={null}
              render={({ field: controllerField }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={controllerField.value === checkboxLabel}
                      onChange={(e) =>
                        controllerField.onChange(
                          e.target.checked ? checkboxLabel : null,
                        )
                      }
                      disabled={disabled}
                    />
                  }
                  label={checkboxLabel}
                />
              )}
            />
            {commonError && <p className={style.errorText}>{commonError}</p>}
          </div>
        );
      }

      case "Time":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              defaultValue={null}
              render={({ field: controllerField }) => {
                const timeValue =
                  typeof controllerField.value === "string"
                    ? dayjs(controllerField.value, "hh:mm A")
                    : controllerField.value;

                return (
                  <TimePicker
                    ampm
                    sx={{ width: "100%" }}
                    value={timeValue}
                    disabled={disabled}
                    onChange={(time) => {
                      if (time && dayjs(time).isValid()) {
                        controllerField.onChange(time.format("hh:mm A"));
                      } else {
                        controllerField.onChange(null);
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: !!commonError,
                        helperText: commonError || "",
                      },
                    }}
                  />
                );
              }}
            />
          </LocalizationProvider>
        );

      case "dropdown":
      case "dispenseDrugKit":
      case "assignedDrugKit":
      case "replaceDrugKit":
        return (
          <div>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              render={({ field: controllerField }) => (
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  disabled={disabled}
                  value={controllerField.value || ""}
                  onChange={(e) => controllerField.onChange(e.target.value)}
                  inputRef={controllerField.ref}
                  onBlur={controllerField.onBlur}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(options || []).map((item, index) => {
                    const isObject = typeof item === "object" && item !== null;
                    const value = isObject ? item.value : item;
                    const label = isObject ? item.text || item?.label : item;

                    return (
                      <MenuItem key={index} value={value}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              )}
            />
            {commonError && <p className={style.errorText}>{commonError}</p>}
          </div>
        );

      case "numericPad":
        return (
          <Controller
            name={name}
            control={control}
            rules={validationRules}
            defaultValue=""
            render={({ field: controllerField }) => (
              <TextField
                size="small"
                fullWidth
                disabled={disabled}
                value={controllerField.value || ""}
                onChange={controllerField.onChange}
                error={!!commonError}
                helperText={commonError || ""}
              />
            )}
          />
        );

      case "singleradio":
        return (
          <div>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              defaultValue={false}
              render={({ field: controllerField }) => (
                <FormControlLabel
                  control={
                    <Radio
                      checked={controllerField.value === true}
                      disabled={disabled}
                      onChange={(e) =>
                        controllerField.onChange(e.target.checked)
                      }
                    />
                  }
                  label=""
                />
              )}
            />
            {commonError && <p className={style.errorText}>{commonError}</p>}
          </div>
        );

      case "genderRadioGroup":
        return (
          <div>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              defaultValue=""
              render={({ field: controllerField }) => (
                <RadioGroup {...controllerField} row>
                  {(options || genderGrpArray).map((item, index) => {
                    const isObject = typeof item === "object" && item !== null;
                    const value = isObject ? item.value : item;
                    const label = isObject
                      ? item.text
                      : item.charAt(0).toUpperCase() + item.slice(1);

                    return (
                      <FormControlLabel
                        key={index}
                        value={value}
                        disabled={disabled}
                        control={<Radio />}
                        label={label}
                      />
                    );
                  })}
                </RadioGroup>
              )}
            />
            {commonError && <p className={style.errorText}>{commonError}</p>}
          </div>
        );

      case "TimeTwo":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              defaultValue={null}
              render={({ field: controllerField }) => (
                <TimePicker
                  sx={{ width: "100%" }}
                  value={controllerField.value || null}
                  disabled={disabled}
                  onChange={(time) => controllerField.onChange(time)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: !!commonError,
                      helperText: commonError || "",
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        );

      case "file":
        return (
          <Controller
            name={name}
            control={control}
            rules={validationRules}
            defaultValue={null}
            render={({ field: controllerField }) => (
              <div>
                <input
                  accept="image/*"
                  type="file"
                  style={{ display: "none" }}
                  disabled={disabled}
                  id={`camera-input-${name}`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      controllerField.onChange(file);
                    }
                  }}
                />
                <label htmlFor={`camera-input-${name}`}>
                  <Button variant="outlined" component="span">
                    Upload / Capture Image
                  </Button>
                </label>
                {commonError && (
                  <p className={style.errorText}>{commonError}</p>
                )}
              </div>
            )}
          />
        );

      case "HotSpot":
      case "None":
      case "NRS":
      case "videourl":
      case "patientVisitDrugKitStatus":
      case "vas":
        return (
          <TextField
            disabled={disabled}
            size="small"
            fullWidth
            type="text"
            error={!!commonError}
            helperText={commonError || ""}
            {...register(name, validationRules)}
          />
        );

      case "visitdatepicker":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              defaultValue={null}
              render={({ field: controllerField }) => (
                <DatePicker
                  sx={{ width: "100%" }}
                  disabled={disabled}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      placeholder: "DD-MM-YYYY",
                      error: !!commonError,
                      helperText: commonError || "",
                    },
                  }}
                  maxDate={dayjs()}
                  value={
                    controllerField.value
                      ? dayjs(controllerField.value, "YYYY-MM-DD")
                      : null
                  }
                  onChange={(date) => {
                    const formatted = date ? date.format("YYYY-MM-DD") : null;
                    controllerField.onChange(formatted);
                  }}
                />
              )}
            />
          </LocalizationProvider>
        );

      case "singleSelectCheckbox":
        return (
          <Controller
            name={name}
            control={control}
            rules={validationRules}
            defaultValue=""
            render={({ field: controllerField }) => (
              <Select
                {...controllerField}
                fullWidth
                size="small"
                displayEmpty
                disabled={disabled}
                renderValue={(selected) => {
                  const found = (options || []).find((item) => {
                    if (typeof item === "object" && item !== null) {
                      return item.value === selected;
                    }
                    return item === selected;
                  });

                  if (!found) return "Select";
                  return typeof found === "object" ? found.text : found;
                }}
              >
                {(options || []).map((item, index) => {
                  const isObject = typeof item === "object" && item !== null;
                  const value = isObject ? item.value : item;
                  const label = isObject ? item.text : item;

                  return (
                    <MenuItem key={value ?? index} value={value}>
                      <Checkbox
                        checked={controllerField?.value === value}
                        sx={{ paddingRight: 1 }}
                      />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}
              </Select>
            )}
          />
        );

      case "headertext":
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: specialProps?.content || specialProps || "",
            }}
          />
        );

      case "paragraphtext":
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: specialProps?.content || specialProps || "",
            }}
          />
        );

      default:
        return (
          <TextField
            disabled={disabled}
            size="small"
            fullWidth
            type="text"
            error={!!commonError}
            helperText={commonError || ""}
            {...register(name, validationRules)}
          />
        );
    }
  };

  return (
    <div className={style.tableWrapper}>
      <table className={style.table}>
        <thead>
          <tr>
            <th className={`${style.thSpacing} ${style.firstCol}`}>Field</th>
            <th className={style.thSpacing}>Current Value</th>
            <th className={style.thSpacing}>Requested Value</th>
            <th className={style.thSpacing}>Remove Value</th>
          </tr>
        </thead>

        <tbody>
          {tableData.map((field, index) => {
            const removeFieldName =
              field?.removeFieldName ||
              `removeValue_${field?.answer_id || index}`;

            return (
              <tr key={field?.answer_id || index} className={style.trBorder}>
                <td className={`${style.thSpacing} ${style.firstCol}`}>
                  {field?.fieldName || field?.label || "-"}
                </td>

                <td className={style.thSpacing}>
                  {field?.currentValue ?? "-"}
                </td>

                <td className={style.thSpacing}>{renderField(field, index)}</td>

                <td className={style.thSpacing}>
                  <Controller
                    name={removeFieldName}
                    control={control}
                    defaultValue={!!field?.removeValue}
                    render={({ field: switchField }) => (
                      <CustomSwitch
                        isSwitch={!!switchField.value}
                        disabled={disabled}
                        setSwitch={(checked) => switchField.onChange(checked)}
                      />
                    )}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const getValidationRules = (field) => {
  const rules = {};

  if (field?.isRequired) {
    rules.required = `${field?.fieldName || field?.label || "This field"} is required`;
  }

  if (field?.fieldType === "text" && field?.specialProps?.maxLength) {
    rules.maxLength = {
      value: field.specialProps.maxLength,
      message: `Maximum ${field.specialProps.maxLength} characters allowed`,
    };
  }

  if (
    [
      "HotSpot",
      "None",
      "NRS",
      "videourl",
      "patientVisitDrugKitStatus",
      "vas",
    ].includes(field?.fieldType) &&
    field?.specialProps?.pattern
  ) {
    rules.pattern = {
      value: new RegExp(field.specialProps.pattern),
      message: field?.specialProps?.patternMessage || "Invalid value",
    };
  }

  return rules;
};

export default DataCorrectionGrid;
